/**
 * Logto OIDC — Authorization Code + PKCE for the browser console.
 *
 * A SPA has no client secret, so the login flow is:
 *
 *   1. `/oidc/auth` with a SHA-256 code_challenge, `offline_access`, the API
 *      resource indicator + its scopes, and `urn:logto:scope:organizations`
 *      -> top-level redirect to Logto's sign-in UI.
 *   2. `/oidc/auth` redirects back to `/auth/callback?code=...&state=...`.
 *   3. Exchange the code (+ code_verifier) for an id_token + **refresh_token**.
 *   4. Exchange that refresh_token AGAIN with `resource` + `organization_id`
 *      + `scope` for an ORGANIZATION-scoped access token. Only that token has
 *      the `organization_id` and `scope` claims the edge gateway requires
 *      (Logto SDK `getAccessTokenByRefreshToken`, verified against 1.26).
 *
 * Rotating refresh tokens are stored locally because Logto rotates them on
 * every exchange. The access token is short-lived; `refreshOrgToken()` is
 * called by the API client on 401.
 *
 * Everything in `oidc` is PUBLIC (a SPA client id is not a secret). Values can
 * be overridden at build time with VITE_* variables; defaults target the
 * production deployment described in infra/deploy/README.md.
 */
import { PRESETS, clearSession, writeSession } from "./session.js";
const env = import.meta.env;
const ORIGIN = typeof window === "undefined" ? "https://vnmade.xbuyer.com" : window.location.origin;
export const oidc = {
    issuer: env.VITE_LOGTO_ISSUER || "https://logto-admin.xbuyer.com/oidc",
    clientId: env.VITE_LOGTO_APP_ID || "74uf1x53imu0n3jwh00b2",
    resource: env.VITE_LOGTO_RESOURCE || "https://vnmade-api.xbuyer.com",
    organizationId: env.VITE_LOGTO_ORG_ID || "02qr4d88f0zt",
    redirectUri: env.VITE_LOGTO_REDIRECT_URI || `${ORIGIN}/auth/callback`,
    postLogoutRedirectUri: env.VITE_LOGTO_POST_LOGOUT_URI || `${ORIGIN}/login`,
    scopes: (env.VITE_LOGTO_SCOPES || "commerce:* rnd:* scm:* mfg:* platform:* ops:*")
        .split(/\s+/)
        .filter(Boolean),
};
const issuerBase = () => oidc.issuer.replace(/\/$/, "");
const PKCE_KEY = "vnmade.oidc.pkce.v1";
function b64url(bytes) {
    let s = "";
    for (const b of bytes)
        s += String.fromCharCode(b);
    return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randomString(bytes) {
    return b64url(crypto.getRandomValues(new Uint8Array(bytes)));
}
/** Decode a JWT payload without verifying it — verification is the gateway's job. */
export function decodeJwt(token) {
    try {
        const part = token.split(".")[1];
        if (!part)
            return {};
        const bin = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
        const bytes = Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    }
    catch {
        return {};
    }
}
function savePending(p) {
    sessionStorage.setItem(PKCE_KEY, JSON.stringify(p));
}
function takePending() {
    const raw = sessionStorage.getItem(PKCE_KEY);
    sessionStorage.removeItem(PKCE_KEY);
    if (!raw)
        return null;
    try {
        const p = JSON.parse(raw);
        if (!p?.state || !p?.verifier || Date.now() - p.issuedAt > 10 * 60 * 1000)
            return null;
        return p;
    }
    catch {
        return null;
    }
}
async function tokenRequest(params) {
    const res = await fetch(`${issuerBase()}/token`, {
        method: "POST",
        // A simple content-type avoids an extra CORS preflight; Logto allows the
        // SPA's redirect origin on /oidc/token (verified with an OPTIONS probe).
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(params).toString(),
    });
    const payload = (await res.json().catch(() => ({})));
    if (!res.ok) {
        throw new Error(`Logto token 请求失败 (${res.status}): ${payload.error_description || payload.error || "unknown error"}`);
    }
    return payload;
}
/**
 * Logto 1.26 requires ALL of these on the authorize request — verified with a
 * three-variant browser probe against the live tenant:
 *  - `offline_access` AND the SPA app's `alwaysIssueRefreshToken` flag
 *    (`customClientMetadata`, set by logto-bootstrap.cjs). Logto strips
 *    offline_access for browser clients otherwise, so the code exchange yields
 *    no refresh_token.
 *  - the API resource indicator AND its scopes: a refresh token cannot be
 *    exchanged for scopes outside its original grant (`400 invalid_scope`).
 *  - `urn:logto:scope:organizations`: RFC 0001 requires it whenever the
 *    refresh-token grant asks for `organization_id` (`403 insufficient_scope`).
 *
 * `organization_id` is deliberately NOT sent to /oidc/auth: Logto ignores it
 * there. The org context is bound by the refresh-token exchange below.
 */
const authorizeScopes = () => ["openid", "profile", "offline_access", "urn:logto:scope:organizations", ...oidc.scopes].join(" ");
/** Step 1 — leave the SPA for Logto's sign-in UI. */
export async function startSignIn(from = "/workspace") {
    const state = randomString(24);
    const verifier = randomString(48);
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
    const challenge = b64url(new Uint8Array(digest));
    savePending({ state, verifier, from, issuedAt: Date.now() });
    const u = new URL(`${issuerBase()}/auth`);
    u.searchParams.set("client_id", oidc.clientId);
    u.searchParams.set("redirect_uri", oidc.redirectUri);
    u.searchParams.set("response_type", "code");
    u.searchParams.set("scope", authorizeScopes());
    u.searchParams.set("resource", oidc.resource);
    u.searchParams.set("state", state);
    u.searchParams.set("code_challenge", challenge);
    u.searchParams.set("code_challenge_method", "S256");
    // organization_id is deliberately NOT sent here: Logto 1.26 ignores it on
    // the authorize request. The org context is bound in the refresh-token
    // exchange below (this is the step most SPA integrations get wrong).
    window.location.assign(u.toString());
}
function presetFromScopes(scopes) {
    const matches = PRESETS.filter((p) => p.scopes.every((s) => scopes.includes(s)));
    matches.sort((a, b) => b.scopes.length - a.scopes.length);
    return matches[0]?.id ?? "buyer";
}
/**
 * Steps 3+4 — code -> refresh token -> org-scoped access token.
 * The resulting Session is written to localStorage and returned.
 */
export async function exchangeOrgToken(refreshToken, subject) {
    const t = await tokenRequest({
        grant_type: "refresh_token",
        client_id: oidc.clientId,
        refresh_token: refreshToken,
        resource: oidc.resource,
        organization_id: oidc.organizationId,
        scope: oidc.scopes.join(" "),
    });
    if (!t.access_token)
        throw new Error("Logto 未下发 org access_token");
    const claims = decodeJwt(t.access_token);
    const scopes = typeof claims.scope === "string" && claims.scope
        ? claims.scope.split(/\s+/).filter(Boolean)
        : oidc.scopes;
    const org = typeof claims.organization_id === "string" ? claims.organization_id : oidc.organizationId;
    const session = {
        token: t.access_token,
        organizationId: org,
        tenantId: `org_${org}`,
        subject: String(claims.sub ?? subject),
        scopes,
        preset: presetFromScopes(scopes),
        issuedAt: new Date().toISOString(),
        mode: "oidc",
        // Logto rotates refresh tokens; always persist the newest one.
        refreshToken: t.refresh_token ?? refreshToken,
        expiresAt: Date.now() + Math.max(30, Number(t.expires_in ?? 3600) - 30) * 1000,
        idToken: t.id_token,
    };
    writeSession(session);
    return session;
}
/** Called by the /auth/callback route with `window.location.search`. */
export async function completeSignIn(search) {
    const q = new URLSearchParams(search);
    const oidcError = q.get("error");
    if (oidcError) {
        throw new Error(`Logto 登录失败：${oidcError} — ${q.get("error_description") ?? ""}`);
    }
    const code = q.get("code");
    const state = q.get("state");
    const pending = takePending();
    if (!code)
        throw new Error("回调缺少 authorization code");
    if (!pending || !state || state !== pending.state) {
        throw new Error("state 校验失败：请重新发起登录（回调不可重复打开，且有效期 10 分钟）");
    }
    const codeRes = await tokenRequest({
        grant_type: "authorization_code",
        client_id: oidc.clientId,
        code,
        code_verifier: pending.verifier,
        redirect_uri: oidc.redirectUri,
    });
    if (!codeRes.refresh_token) {
        throw new Error("Logto 未下发 refresh_token（SPA 应用需 offline_access + PKCE）");
    }
    const idClaims = decodeJwt(codeRes.id_token ?? "");
    const session = await exchangeOrgToken(codeRes.refresh_token, String(idClaims.sub ?? "logto_user"));
    // Keep the id_token from the code exchange so a later RP-initiated logout can
    // present it as id_token_hint.
    if (codeRes.id_token && !session.idToken) {
        session.idToken = codeRes.id_token;
        writeSession(session);
    }
    return { session, from: pending.from || "/workspace" };
}
/** Called by the API client after a 401, or proactively before expiry. */
export async function refreshOrgToken(session) {
    if (session.mode !== "oidc" || !session.refreshToken)
        return null;
    try {
        return await exchangeOrgToken(session.refreshToken, session.subject);
    }
    catch {
        clearSession();
        return null;
    }
}
/** Optional RP-initiated logout (local session is always cleared first). */
export function endSessionUrl(idTokenHint) {
    // Logto 1.26 advertises `end_session_endpoint: <issuer>/session/end` in
    // discovery — NOT `/oidc/logout`. post_logout_redirect_uri must be one of the
    // SPA application's registered postLogoutRedirectUris.
    const u = new URL(`${issuerBase()}/session/end`);
    u.searchParams.set("client_id", oidc.clientId);
    u.searchParams.set("post_logout_redirect_uri", oidc.postLogoutRedirectUri);
    if (idTokenHint)
        u.searchParams.set("id_token_hint", idTokenHint);
    return u.toString();
}
